import { apiClient } from '@littlebird/auth-client';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/components/modals/ConfirmationModal';
import { ModalsManager } from '@/components/modals/modalsManager';
import { confirmTeamChatDeletion } from '@/features/team-chats/confirm-team-chat-deletion';
import { queryClient } from '@/query/client';
import { userStore } from '@/stores/user.store';

import type {
  ProactiveAgentInstance,
  ProactiveAgents,
} from './proactive-agent.query';

export const agentsQueryKey = () => [
  'proactive-agent',
  'instances',
  userStore.userId,
];

export const fetchProactiveAgents = () =>
  apiClient.get<ProactiveAgents>('/proactive_agent/instances');

/** Asks the user to confirm deleting one agent chat, which also deletes its routines and their action cards */
export const confirmProactiveChatDeletion = async (
  instance: ProactiveAgentInstance
): Promise<boolean> =>
  Boolean(
    await ModalsManager.showResolvable(ConfirmDialog, {
      title: 'Delete chat?',
      description: `This permanently deletes the chat ${instance.chat_id.slice(0, 8)} of ${instance.agent}, its routines and their run history, and the action cards those runs produced. This cannot be undone.`,
      confirmText: 'Delete chat',
      cancelText: 'Cancel',
    })
  );

/**
 * Asks the user to confirm before chats are deleted from a chat list, when any of them belongs to a
 * proactive agent, because its routines and their action cards are deleted with it. Other chats need
 * no extra confirmation, so this resolves true for them. When the agent list cannot be loaded, it
 * resolves false, so nothing is deleted without the warning.
 */
export const confirmChatListDeletion = async (
  chatIds: readonly string[]
): Promise<boolean> => {
  if (!(await confirmTeamChatDeletion(chatIds))) return false;
  // Only internal users can have agent chats
  if (!userStore.isInternalUser) return true;
  let agents: ProactiveAgents;
  try {
    // Fetched fresh, so a chat created on another device a moment ago still gets the warning
    agents = await queryClient.fetchQuery({
      queryKey: agentsQueryKey(),
      queryFn: fetchProactiveAgents,
    });
  } catch {
    toast.error(
      'Could not check whether this chat belongs to a proactive agent. Try again.'
    );
    return false;
  }
  const instances = agents.instances;
  const agentChats = instances.filter((instance) =>
    chatIds.includes(instance.chat_id)
  );
  if (agentChats.length === 0) return true;
  if (agentChats.length === 1)
    return confirmProactiveChatDeletion(agentChats[0]);
  return Boolean(
    await ModalsManager.showResolvable(ConfirmDialog, {
      title: 'Delete chats?',
      description: `${agentChats.length} of these chats belong to proactive agents. Deleting them also deletes their routines, the routines' run history, and the action cards those runs produced. This cannot be undone.`,
      confirmText: 'Delete chats',
      cancelText: 'Cancel',
    })
  );
};
